import type { RwgConversionPayload } from './reportRwgConversion';
import { getRwg } from './rwgStore';
import { MERCHANT_CHANGED, MERCHANT_UNCHANGED } from './types';

/** Every identifier the booked shop is known by at checkout. */
export interface BookedShop {
  /** Whatever the checkout call was given — a GUID on the main booking path, a
   * route slug on the Cash App return path. */
  id: string;
  /** The shop's route slug, when the caller knows it. */
  route?: string;
}

/**
 * Builds the conversion payload for a completed booking, or `null` when this
 * booking did not come from a Reserve with Google link.
 *
 * The captured shop is the URL's route slug — that is all a Reserve with Google
 * landing URL carries. The booked shop arrives as a GUID on the main booking
 * path and as a slug on the Cash App return path, so the comparison accepts a
 * match on any identifier rather than assuming one value space. Getting this
 * wrong reports `merchant_changed: 1` for a customer who booked exactly the
 * shop Google showed them.
 *
 * Reports "unchanged" when there is nothing to compare — a token captured
 * without a shop, or a checkout with no shop identifier at all. Unchanged is
 * Google's default reading, and there is no evidence the customer switched.
 */
export const buildRwgConversion = (
  bookedShop: BookedShop,
): RwgConversionPayload | null => {
  const data = getRwg();
  if (!data) return null;

  const bookedIdentifiers = [bookedShop.id, bookedShop.route].filter(Boolean);

  const merchantChanged =
    data.shopId &&
    bookedIdentifiers.length > 0 &&
    !bookedIdentifiers.includes(data.shopId)
      ? MERCHANT_CHANGED
      : MERCHANT_UNCHANGED;

  return { token: data.token, merchantChanged };
};
